import { supabase } from '@/integrations/supabase/client';

// Tracking enabled cache
let trackingEnabledCache: boolean | null = null;
let trackingCacheExpiry: number = 0;
const CACHE_TTL = 60000; // 1 minute cache

// Check if tracking is enabled (with caching)
const isTrackingEnabled = async (): Promise<boolean> => {
  const now = Date.now();
  if (trackingEnabledCache !== null && now < trackingCacheExpiry) {
    return trackingEnabledCache;
  }
  
  try {
    const { data, error } = await supabase
      .from('site_settings')
      .select('analytics_tracking_enabled')
      .limit(1)
      .maybeSingle();
    
    if (error) {
      console.error('Error fetching tracking setting:', error);
      return true; // Default to enabled on error
    }
    
    trackingEnabledCache = data?.analytics_tracking_enabled ?? true;
    trackingCacheExpiry = now + CACHE_TTL;
    return trackingEnabledCache;
  } catch (error) {
    console.error('Error checking tracking status:', error);
    return true;
  }
};

// Force refresh the tracking enabled cache
export const refreshTrackingCache = (): void => {
  trackingEnabledCache = null;
  trackingCacheExpiry = 0;
};

// Event categories for grouping
export type EventCategory = 
  | 'conversion'
  | 'engagement'
  | 'navigation'
  | 'discovery'
  | 'ui_interaction';

// Predefined event types
export type EventType =
  // Conversion events
  | 'add_to_cart'
  | 'remove_from_cart'
  | 'update_cart_quantity'
  | 'checkout_started'
  | 'checkout_completed'
  | 'payment_failed'
  // Engagement events
  | 'favorite_added'
  | 'favorite_removed'
  | 'chatbot_opened'
  | 'chatbot_message_sent'
  | 'chatbot_feedback'
  | 'quick_action_clicked'
  // Navigation events
  | 'page_view'
  | 'category_filter_applied'
  | 'test_method_filter_applied'
  | 'sort_changed'
  // Discovery events
  | 'search_performed'
  | 'search_result_clicked'
  | 'recommendation_clicked'
  | 'upsell_viewed'
  | 'upsell_clicked'
  // UI interaction events
  | 'cta_clicked'
  | 'cart_sidebar_opened'
  | 'newsletter_signup'
  | 'support_dialog_opened'
  | 'external_link_clicked';

// Metadata types for specific events
export interface EventMetadata {
  // Cart events
  quantity?: number;
  old_quantity?: number;
  new_quantity?: number;
  source?: string;
  // Checkout events
  cart_value?: number;
  item_count?: number;
  order_id?: string;
  order_value?: number;
  error_reason?: string;
  // Chatbot events
  trigger?: string;
  message_length?: number;
  feedback_type?: 'up' | 'down';
  // Search events
  query?: string;
  results_count?: number;
  result_position?: number;
  // Filter events
  category_name?: string;
  method_name?: string;
  sort_option?: string;
  // UI events
  button_label?: string;
  page_context?: string;
  link_url?: string;
  link_type?: string;
  action?: string;
  action_label?: string;
  action_question?: string;
  // Recommendation/upsell
  recommendation_type?: string;
  current_product?: string;
  upgrade_product?: string;
  // Page view events
  page_path?: string;
  page_search?: string;
  referrer?: string;
  previous_page?: string;
  time_spent_seconds?: number;
  event_subtype?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  // Generic
  [key: string]: string | number | boolean | undefined;
}

export interface TrackEventParams {
  type: EventType;
  productId?: string;
  category?: EventCategory;
  metadata?: EventMetadata;
  sessionId: string;
  userId?: string;
}

// Event queue for batching low-priority events
let eventQueue: TrackEventParams[] = [];
let flushTimeout: ReturnType<typeof setTimeout> | null = null;

// High-priority events that should be sent immediately
const HIGH_PRIORITY_EVENTS: EventType[] = [
  // Conversion events
  'add_to_cart',
  'remove_from_cart',
  'update_cart_quantity',
  'checkout_started',
  'checkout_completed',
  'payment_failed',
  // Engagement events
  'favorite_added',
  'favorite_removed',
  'chatbot_opened',
  'chatbot_message_sent',
  // Discovery events
  'search_performed',
  'search_result_clicked',
  'recommendation_clicked',
  'upsell_clicked',
];

// Flush the event queue to the database
const flushEvents = async () => {
  if (eventQueue.length === 0) return;

  const eventsToFlush = [...eventQueue];
  eventQueue = [];

  try {
    const { error } = await supabase
      .from('user_interactions')
      .insert(
        eventsToFlush.map(event => ({
          session_id: event.sessionId,
          user_id: event.userId || null,
          product_id: event.productId || null,
          interaction_type: event.type,
          category: event.category || null,
          metadata: event.metadata || {},
        }))
      );

    if (error) {
      console.error('Error flushing analytics events:', {
        code: error.code,
        message: error.message,
        details: error.details,
        eventCount: eventsToFlush.length,
        eventTypes: eventsToFlush.map(e => e.type),
      });
      // Re-queue failed events (with a cap to prevent infinite growth)
      if (eventQueue.length < 100) {
        eventQueue = [...eventsToFlush, ...eventQueue];
      }
    } else {
      console.debug('Flushed analytics events:', eventsToFlush.map(e => e.type));
    }
  } catch (error) {
    console.error('Error flushing analytics events:', error);
  }
};

// Schedule a flush if not already scheduled
const scheduleFlush = () => {
  if (flushTimeout) return;
  flushTimeout = setTimeout(() => {
    flushTimeout = null;
    flushEvents();
  }, 2000); // Batch events every 2 seconds
};

/**
 * Track a user interaction event
 */
export const trackEvent = async (params: TrackEventParams): Promise<void> => {
  // Check if tracking is enabled
  const enabled = await isTrackingEnabled();
  if (!enabled) {
    console.debug('Analytics tracking is paused');
    return;
  }
  const isHighPriority = HIGH_PRIORITY_EVENTS.includes(params.type);

  if (isHighPriority) {
    // Send immediately for high-priority events
    try {
      const { error } = await supabase
        .from('user_interactions')
        .insert({
          session_id: params.sessionId,
          user_id: params.userId || null,
          product_id: params.productId || null,
          interaction_type: params.type,
          category: params.category || null,
          metadata: params.metadata || {},
        });

      if (error) {
        console.error('Error tracking event:', params.type, {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
        });
      } else {
        console.debug('Event tracked:', params.type, params.metadata);
      }
    } catch (error) {
      console.error('Error tracking event:', params.type, error);
    }
  } else {
    // Queue low-priority events for batching
    eventQueue.push(params);
    scheduleFlush();
  }
};

// Flush events before page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (flushTimeout) {
      clearTimeout(flushTimeout);
    }
    // Use sendBeacon for reliable delivery on page unload
    if (eventQueue.length > 0 && navigator.sendBeacon) {
      const payload = JSON.stringify({
        events: eventQueue.map(event => ({
          session_id: event.sessionId,
          user_id: event.userId || null,
          product_id: event.productId || null,
          interaction_type: event.type,
          category: event.category || null,
          metadata: event.metadata || {},
        }))
      });
      // Note: This would need a dedicated endpoint for beacon
      // For now, we'll just try a synchronous flush
      flushEvents();
    }
  });
}
